import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiEdit2 } from 'react-icons/fi';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import ToggleButton from '../../layout/ReusableComponent/Buttons/ToggleButton';
import TextEditor from '../../layout/ReusableComponent/Text-editor/TextEditor';
import { commentBodyIsEffectivelyEmpty } from '../../layout/ReusableComponent/Text-editor/textEditorBodyUtils';
import { useAppShellHeaderOffset } from '../../layout/ReusableComponent/audioPlayer/useAppShellHeaderOffset';
import {
  normalizeStorageBoxNewFileName,
  storageBoxFileFromEditorPayload,
} from '../utils/storageBoxNewTextFile';
import { storageBoxApi } from '../api/storageBoxApi';
import { useStorageBox } from '../StorageBoxContext';
import { fetchAndDecryptLockText } from '../utils/lockFileAccess';
import {
  isStorageBoxEditableTextFile,
  serializeEditorPayloadForSave,
  storageBoxTextEditorKind,
  textPayloadFromLoadedContent,
} from '../utils/storageBoxTextFileEditor';
import {
  readStorageBoxTextEditorAutosaveEnabled,
  writeStorageBoxTextEditorAutosaveEnabled,
  STORAGE_BOX_TEXT_EDITOR_AUTOSAVE_DEBOUNCE_MS,
} from '../utils/storageBoxTextEditorAutosave';
import StorageBoxTextEditorLocation, {
  StorageBoxTextEditorFileIcon,
} from './StorageBoxTextEditorLocation';

const EDITOR_VISIBLE = {
  flag: false,
  cancel: false,
};

export function isStorageBoxTextEditorItem(item) {
  return isStorageBoxEditableTextFile(item);
}

export default function StorageBoxTextEditorModal({ isOpen, onClose, item }) {
  const {
    readOnly,
    writable,
    browseMode,
    inLockVault,
    lockSessionToken,
    lockMasterKeyReady,
    getLockMasterKeyBytes,
    reloadCurrentView,
    renameItem,
    notify,
  } = useStorageBox();

  const show = isOpen && isStorageBoxTextEditorItem(item);
  useAppShellHeaderOffset(show);

  const editorKind = useMemo(() => storageBoxTextEditorKind(item), [item]);
  const isRich = editorKind === 'rich';

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [initialHtml, setInitialHtml] = useState('');
  const [initialMarkdown, setInitialMarkdown] = useState('');
  const [initialDocumentPageSettings, setInitialDocumentPageSettings] = useState(null);
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(() => readStorageBoxTextEditorAutosaveEnabled());
  const renameInputRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const autosaveEnabledRef = useRef(autosaveEnabled);
  const savingRef = useRef(false);

  const payloadRef = useRef(textPayloadFromLoadedContent('', editorKind));
  const baselineRef = useRef('');

  const canEdit = !readOnly && writable && !item?.isEncrypted;

  const canRename = useMemo(() => {
    if (!item || readOnly) return false;
    if (browseMode === 'bin') return writable;
    if (item.canManage) return true;
    return writable && (browseMode === 'lock' || browseMode === 'shared');
  }, [item, readOnly, writable, browseMode]);

  useEffect(() => {
    autosaveEnabledRef.current = autosaveEnabled;
  }, [autosaveEnabled]);

  useEffect(
    () => () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    setDisplayName(item?.name || 'Document');
    setRenaming(false);
    setRenameDraft('');
    setRenameBusy(false);
  }, [item?.itemId, item?.name]);

  useEffect(() => {
    if (!renaming) return undefined;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
    return undefined;
  }, [renaming]);

  useEffect(() => {
    if (!show || !item?.itemId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setDirty(false);

    const applyText = (text) => {
      if (cancelled) return;
      const kind = storageBoxTextEditorKind(item);
      const payload = textPayloadFromLoadedContent(text, kind);
      payloadRef.current = payload;
      baselineRef.current = serializeEditorPayloadForSave(payload, kind);
      setInitialHtml(kind === 'rich' ? payload.html || '' : '');
      setInitialMarkdown(kind === 'rich' ? '' : payload.markdown || '');
      setInitialDocumentPageSettings(payload.documentPageSettings ?? null);
      setEditorEpoch((n) => n + 1);
      setLoading(false);
    };

    if (inLockVault && lockSessionToken && item.isEncrypted) {
      if (!lockMasterKeyReady) {
        setLoadError('Unlock Lock folder to open this file.');
        setLoading(false);
        return undefined;
      }
      const masterKey = getLockMasterKeyBytes();
      if (!masterKey) {
        setLoadError('Unlock Lock folder to open this file.');
        setLoading(false);
        return undefined;
      }
      fetchAndDecryptLockText(item, lockSessionToken, masterKey)
        .then(applyText)
        .catch((e) => {
          if (!cancelled) {
            setLoadError(e.message || 'Could not open file');
            setLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    storageBoxApi
      .previewText(item.itemId)
      .then((r) => {
        if (cancelled) return;
        if (r.tooLarge) {
          setLoadError('File too large to open in the editor.');
          setLoading(false);
          return;
        }
        applyText(r.text || '');
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(e.message || 'Could not open file');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    show,
    item,
    inLockVault,
    lockSessionToken,
    lockMasterKeyReady,
    getLockMasterKeyBytes,
  ]);

  const persistContent = useCallback(
    async ({ silent = false, notifyOnSuccess = !silent } = {}) => {
      if (!canEdit || !item?.itemId || savingRef.current) return false;
      const text = serializeEditorPayloadForSave(payloadRef.current, editorKind);
      if (commentBodyIsEffectivelyEmpty(text)) {
        if (!silent) notify('Cannot save an empty file');
        return false;
      }
      if (text === baselineRef.current) {
        setDirty(false);
        return true;
      }
      savingRef.current = true;
      setSaving(true);
      try {
        await storageBoxApi.saveTextContent(item.itemId, text);
        baselineRef.current = text;
        setDirty(false);
        await reloadCurrentView();
        if (notifyOnSuccess) notify('Saved');
        return true;
      } catch (e) {
        notify(e.message || 'Save failed');
        return false;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [canEdit, item?.itemId, editorKind, reloadCurrentView, notify]
  );

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  const scheduleAutosave = useCallback(() => {
    if (!autosaveEnabledRef.current || !canEdit) return;
    clearAutosaveTimer();
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void persistContent({ silent: true, notifyOnSuccess: false });
    }, STORAGE_BOX_TEXT_EDITOR_AUTOSAVE_DEBOUNCE_MS);
  }, [canEdit, clearAutosaveTimer, persistContent]);

  const handleAutosaveToggle = useCallback(
    (next) => {
      setAutosaveEnabled(next);
      writeStorageBoxTextEditorAutosaveEnabled(next);
      if (!next) {
        clearAutosaveTimer();
      } else {
        scheduleAutosave();
      }
    },
    [clearAutosaveTimer, scheduleAutosave]
  );

  const onChange = useCallback(
    (payload) => {
      payloadRef.current = payload;
      const next = serializeEditorPayloadForSave(payload, editorKind);
      const isDirty = next !== baselineRef.current;
      setDirty(isDirty);
      if (isDirty && autosaveEnabledRef.current) {
        scheduleAutosave();
      }
    },
    [editorKind, scheduleAutosave]
  );

  const handleSave = useCallback(async () => {
    clearAutosaveTimer();
    await persistContent({ silent: false, notifyOnSuccess: true });
  }, [clearAutosaveTimer, persistContent]);

  const handleSaveAsStorageBox = useCallback(async () => {
    if (!canEdit || savingRef.current) return;
    clearAutosaveTimer();
    const text = serializeEditorPayloadForSave(payloadRef.current, editorKind);
    if (commentBodyIsEffectivelyEmpty(text)) {
      notify('Cannot save an empty file');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const kind = isRich ? 'rich' : 'plain';
      const normalizedName = normalizeStorageBoxNewFileName(displayName || item?.name || 'Untitled', kind);
      const file = storageBoxFileFromEditorPayload(payloadRef.current, kind, normalizedName);
      await storageBoxApi.uploadFiles(null, [file], null);
      await reloadCurrentView();
      notify('Saved copy to Storage Box');
    } catch (e) {
      notify(e.message || 'Save failed');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [
    canEdit,
    clearAutosaveTimer,
    editorKind,
    isRich,
    displayName,
    item?.name,
    reloadCurrentView,
    notify,
  ]);

  const startRename = useCallback(() => {
    if (!canRename || renameBusy || saving) return;
    setRenameDraft(displayName || item?.name || '');
    setRenaming(true);
  }, [canRename, renameBusy, saving, displayName, item?.name]);

  const cancelRename = useCallback(() => {
    if (renameBusy) return;
    setRenaming(false);
    setRenameDraft('');
  }, [renameBusy]);

  const submitRename = useCallback(async () => {
    if (!renaming || renameBusy || !canRename || !item) return;
    const trimmed = renameDraft.trim();
    if (!trimmed) return;
    if (trimmed === (item.name || displayName)) {
      cancelRename();
      return;
    }
    setRenameBusy(true);
    try {
      await renameItem(item, trimmed);
      setDisplayName(trimmed);
      setRenaming(false);
      setRenameDraft('');
    } catch (e) {
      notify(e.message || 'Rename failed');
    } finally {
      setRenameBusy(false);
    }
  }, [renaming, renameBusy, canRename, item, renameDraft, displayName, cancelRename, renameItem, notify]);

  const handleRenameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelRename();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submitRename();
      }
    },
    [cancelRename, submitRename]
  );

  const handleFileMenuNew = useCallback(() => {
    if (!canEdit || saving) return;
    clearAutosaveTimer();
    setInitialHtml('');
    setInitialMarkdown('');
    setInitialDocumentPageSettings(null);
    const empty = textPayloadFromLoadedContent('', editorKind);
    payloadRef.current = empty;
    baselineRef.current = serializeEditorPayloadForSave(empty, editorKind);
    setDirty(false);
    setEditorEpoch((epoch) => epoch + 1);
  }, [canEdit, saving, clearAutosaveTimer, editorKind]);

  const handleFileMenuDocumentLoaded = useCallback(
    (parsed) => {
      if (!canEdit) return;
      const nextName = String(parsed?.title || displayName || item?.name || 'Document').trim();
      if (nextName && nextName !== displayName) {
        setDisplayName(nextName);
      }
      setDirty(true);
    },
    [canEdit, displayName, item?.name]
  );

  const handleRequestClose = useCallback(() => {
    if (renaming) {
      cancelRename();
      return;
    }
    clearAutosaveTimer();
    if (autosaveEnabled && dirty && canEdit) {
      void persistContent({ silent: true, notifyOnSuccess: false }).finally(() => {
        onClose();
      });
      return;
    }
    onClose();
  }, [renaming, cancelRename, clearAutosaveTimer, autosaveEnabled, dirty, canEdit, persistContent, onClose]);

  const overlayClassName = 'sb-text-editor-modal-overlay--sheet';
  const contentClassName = 'sb-text-editor-modal sb-text-editor-modal--sheet';

  const contentStyle = useMemo(
    () => ({
      width: '100%',
      maxWidth: '100%',
      height: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
      maxHeight: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
      minHeight: 0,
      margin: 0,
      borderRadius: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }),
    []
  );

  if (!show) return null;

  const title = displayName || item?.name || 'Document';

  return (
    <CenterModalWindow
      isOpen={show}
      onClose={handleRequestClose}
      width="100%"
      height="100%"
      overlayClassName={overlayClassName}
      contentClassName={contentClassName}
      contentStyle={contentStyle}
      closeButtonTooltip="Close editor"
    >
      <div className="sb-text-editor-modal__body" data-modal-scroll-port>
        <header className="sb-text-editor-modal__head">
          <div className="sb-text-editor-modal__head-bar">
            <div className="sb-text-editor-modal__title-block">
              <StorageBoxTextEditorLocation />
              <div className="sb-text-editor-modal__title-row">
              {renaming && canRename ? (
              <>
                <StorageBoxTextEditorFileIcon item={item} kind={isRich ? 'rich' : 'plain'} />
                <input
                  ref={renameInputRef}
                  type="text"
                  className="sb-text-editor-modal__title-input sb-preview__title-input"
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={handleRenameKeyDown}
                  maxLength={240}
                  disabled={renameBusy}
                  aria-label="File name"
                />
                <div className="sb-text-editor-modal__rename-actions">
                  <button
                    type="button"
                    className="sb-text-editor-modal__rename-apply"
                    onClick={submitRename}
                    disabled={renameBusy || !renameDraft.trim()}
                    title="Apply new file name"
                    aria-label="Apply new file name"
                  >
                    <FiCheck aria-hidden />
                    <span>Apply name</span>
                  </button>
                  <button
                    type="button"
                    className="sb-text-editor-modal__rename-cancel"
                    onClick={cancelRename}
                    disabled={renameBusy}
                    title="Discard name change"
                    aria-label="Discard name change"
                  >
                    Cancel edit
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="sb-text-editor-modal__filename">
                  <StorageBoxTextEditorFileIcon item={item} kind={isRich ? 'rich' : 'plain'} />
                  <h2 className="sb-text-editor-modal__title" title={title}>
                    {title}
                  </h2>
                </div>
                {canRename ? (
                  <div className="sb-preview__head-actions">
                    <button
                      type="button"
                      className="sb-preview__head-action"
                      onClick={startRename}
                      disabled={saving}
                      title="Rename file"
                      aria-label="Rename file"
                    >
                      <FiEdit2 aria-hidden />
                    </button>
                  </div>
                ) : null}
              </>
            )}
              </div>
            </div>
            {canEdit && !loadError ? (
              <div
                className="sb-text-editor-modal__autosave-row"
                role="group"
                aria-label={`Autosave, ${autosaveEnabled ? 'on' : 'off'}`}
              >
                <span className="sb-text-editor-modal__autosave-label">Autosave</span>
                <ToggleButton
                  checked={autosaveEnabled}
                  onToggle={handleAutosaveToggle}
                  disabled={saving || loading}
                  className="sb-text-editor-modal__autosave-toggle"
                />
                <span className="sb-text-editor-modal__autosave-status" aria-live="polite">
                  {saving
                    ? 'Saving…'
                    : autosaveEnabled
                      ? dirty
                        ? 'Unsaved'
                        : 'Saved'
                      : dirty
                        ? 'Manual'
                        : null}
                </span>
              </div>
            ) : null}
          </div>
          {loadError ? <p className="sb-text-editor-modal__error">{loadError}</p> : null}
          {item?.isEncrypted ? (
            <p className="sb-text-editor-modal__hint">Encrypted Lock files are view-only in the editor.</p>
          ) : null}
        </header>
        <div className="sb-text-editor-modal__editor">
          {loading ? (
            <p className="sb-text-editor-modal__loading">Loading…</p>
          ) : loadError ? null : (
            <TextEditor
                key={`${item.itemId}-${editorEpoch}`}
                className="sb-text-editor-modal__text-editor"
                lockEditorMode
                documentExport
                initialDocumentPageSettings={initialDocumentPageSettings}
                exportBaseFileName={title}
                defaultMode={isRich ? 'rich' : 'markdown'}
                defaultToolbarVisible
                showContentFlags={false}
                visible={EDITOR_VISIBLE}
                initialHtml={initialHtml}
                initialMarkdown={initialMarkdown}
                richPlaceholder={isRich ? 'Write HTML or rich text…' : 'Write plain text or markdown…'}
                markdownPlaceholder="Write plain text or markdown…"
                onChange={onChange}
                fileMenuUserLoggedIn
                fileMenuAutosaveEnabled={autosaveEnabled}
                onFileMenuSave={canEdit ? handleSave : undefined}
                onFileMenuSaveAsStorageBox={canEdit ? handleSaveAsStorageBox : undefined}
                onFileMenuNew={canEdit ? handleFileMenuNew : undefined}
                onFileMenuExit={handleRequestClose}
                onFileMenuDocumentLoaded={handleFileMenuDocumentLoaded}
                primaryActionText={
                  canEdit && !autosaveEnabled ? (saving ? 'Saving…' : 'Save') : ''
                }
                onPrimaryAction={canEdit && !autosaveEnabled ? handleSave : undefined}
                primaryActionDisabled={!canEdit || saving || !dirty}
                primaryActionsOnRight
                submitOnModifierEnter={canEdit && !autosaveEnabled}
              />
          )}
        </div>
      </div>
    </CenterModalWindow>
  );
}
